import {
  normaliseName,
  type RenameVocabularyResponse,
  type VocabularyKind,
  type VocabularyResponse,
  type VocabularyRow,
} from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { fetchVocabulary, renameVocabulary } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { actionButton, control, resting } from "../components/controls.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import { useReload } from "../offline.js";

/**
 * Task 9.13 — the words this cellar has decided, and correcting one.
 *
 * Six things here are reference rows created by being typed, and until now not
 * one of them could be corrected: a producer entered as `Vivnio` was suggested
 * by the picker forever, and the only way out was to retype the right word on
 * every wine and leave the wrong one standing.
 *
 * **One field per row, and no second button.** Renaming and merging are the
 * same act — every row is unique on its cellar and sort key, so a name another
 * row already holds cannot produce two rows. What the field does is decided by
 * what is typed into it, which is why there is nothing to choose between. The
 * sentence under the field says which of the two is about to happen, computed
 * from the same sort keys the server matches on.
 *
 * **It says the count before, and offers nothing after.** A merge moves wines
 * or lots and deletes a spelling; no bottle changes and no wine number moves,
 * so there is nothing an undo would restore. Deleting a lot is still the only
 * thing in this application that asks twice, and it is the one that takes
 * bottles with it.
 *
 * Reached from *Producenter* rather than standing in the row of sections: that
 * row already wraps onto three lines on a phone, and producers are the
 * vocabulary anybody comes looking for first.
 */
const KINDS: readonly { kind: VocabularyKind; title: string }[] = [
  { kind: "producers", title: "vocabulary.producers" },
  { kind: "regions", title: "vocabulary.regions" },
  { kind: "grapes", title: "vocabulary.grapes" },
  { kind: "styles", title: "vocabulary.styles" },
  { kind: "ratingSources", title: "vocabulary.ratingSources" },
  { kind: "purchaseSources", title: "vocabulary.purchaseSources" },
];

type State =
  | { status: "loading" }
  | { status: "ready"; vocabulary: VocabularyResponse }
  | { status: "failed" };

export function Vocabulary() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [editing, setEditing] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const mayWrite = useMayWrite();

  const load = useCallback(() => {
    void fetchVocabulary(cellarId)
      .then((vocabulary) => setState({ status: "ready", vocabulary }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  // A guest reads the cellar's wine and not the running of it. This page is
  // the running of it: the words are the cellar's own decisions.
  if (mayWrite === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="vocabulary" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="vocabulary" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("vocabulary.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("vocabulary.loadFailed")}
        </p>
      )}

      {said !== null && (
        <p role="status" className="text-ink mt-4 max-w-prose text-sm">
          {said}
        </p>
      )}

      {state.status === "ready" &&
        KINDS.map(({ kind, title }) => {
          const rows = state.vocabulary[kind];
          return (
            <section key={kind} className="mt-10">
              <h2 className="font-display border-line border-b pb-2 text-lg">
                {t(title)}
              </h2>
              {rows.length === 0 ? (
                <p className="text-muted mt-3 max-w-prose text-sm">
                  {t("vocabulary.none")}
                </p>
              ) : (
                <ul className="border-line">
                  {rows.map((row) => (
                    <li key={row.id} className="border-line border-b py-3 text-sm">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="font-display text-base">{row.name}</span>
                        <span className="text-muted flex items-baseline gap-4">
                          <span>{t("vocabulary.uses", { count: row.uses })}</span>
                          {editing !== row.id && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditing(row.id);
                                setSaid(null);
                              }}
                              className="text-accent underline-offset-4 hover:underline"
                            >
                              {t("vocabulary.rename")}
                            </button>
                          )}
                        </span>
                      </div>

                      {editing === row.id && (
                        <RenameForm
                          cellarId={cellarId}
                          kind={kind}
                          row={row}
                          siblings={rows}
                          onCancel={() => setEditing(null)}
                          onDone={(answer) => {
                            setEditing(null);
                            setSaid(
                              answer.outcome === "renamed"
                                ? t("vocabulary.renamed", { name: answer.name })
                                : t("vocabulary.merged", {
                                    name: answer.name,
                                    count: answer.moved,
                                  }) +
                                    (answer.dropped > 0
                                      ? ` ${t("vocabulary.dropped", { count: answer.dropped })}`
                                      : ""),
                            );
                            load();
                          }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
    </section>
  );
}

/**
 * The one field, and the sentence that says what it is about to do.
 *
 * The sentence is computed here rather than asked for, from the same sort keys
 * the server matched on and sent down with the rows. That is deliberate: a
 * round trip per keystroke in front of a container that may be asleep is the
 * thing `tech-stack.md` refuses for near-match detection generally, and the
 * rule is small enough to hold in one place — `normaliseName`, which lives in
 * `shared/` precisely so both sides can apply it and get the same answer.
 */
function RenameForm({
  cellarId,
  kind,
  row,
  siblings,
  onDone,
  onCancel,
}: {
  cellarId: string;
  kind: VocabularyKind;
  row: VocabularyRow;
  siblings: readonly VocabularyRow[];
  onDone: (answer: RenameVocabularyResponse) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(row.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortKey = normaliseName(name);
  // A region matches within its own country as well, because that is what its
  // index says makes two rows the same row: Rioja in Spain and a Rioja
  // somewhere else are not the same place.
  const target = siblings.find(
    (candidate) =>
      candidate.id !== row.id &&
      candidate.sortKey === sortKey &&
      candidate.countryCode === row.countryCode,
  );
  const changed = sortKey !== row.sortKey || name !== row.name;
  const refused = target !== undefined && (!row.mergeable || !target.mergeable);
  const canSubmit = name.trim() !== "" && changed && !refused && !busy;

  return (
    <form
      className="mt-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        void renameVocabulary(cellarId, kind, row.id, name)
          .then(onDone)
          .catch(() => setError(t("vocabulary.saveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="sr-only" htmlFor={`rename-${row.id}`}>
          {t("vocabulary.renameOf", { name: row.name })}
        </label>
        {/* Its own line on a phone, so the two buttons stay on one. A 16rem
            field plus Spara fills the width at 412px and leaves Avbryt
            stranded alone underneath, reading as something gone wrong rather
            than as the way out. */}
        <div className="w-full sm:w-64">
          <input
            id={`rename-${row.id}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={`${control} ${resting}`}
          />
        </div>
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("vocabulary.saving") : t("vocabulary.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink underline-offset-4 hover:underline"
        >
          {t("wines.cancel")}
        </button>
      </div>

      {/* What is about to happen, in the words of what it does to the cellar.
          Three sentences and not one: renaming keeps every reference, merging
          moves them, and the one row that may never be merged says so instead
          of failing at the button. */}
      <p className="text-muted mt-2 max-w-prose">
        {refused
          ? t("vocabulary.ownRatingSource")
          : target !== undefined
            ? t("vocabulary.willMerge", { name: target.name, count: row.uses })
            : changed
              ? t("vocabulary.willRename", { count: row.uses })
              : t("vocabulary.unchanged")}
      </p>

      {error !== null && (
        <p role="alert" className="text-past mt-2">
          {error}
        </p>
      )}
    </form>
  );
}
